"use client";

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"
import { motion } from "framer-motion"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all cursor-pointer disabled:cursor-not-allowed disabled:bg-[#D1D1D6] [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
    {
        variants: {
            variant: {
                default: "text-white border-2 border-black rounded-lg shadow-[0_4px_0_0_black] cursor-pointer transition-all duration-150 active:translate-y-[4px] bg-primary active:shadow-none",
                destructive:
                    "bg-red-600 text-white hover:bg-red-700 rounded-md border border-transparent",
                outline:
                    "border border-black text-[#667085] bg-white hover:bg-gray-50 rounded-md active:border-primary transition-colors duration-150",
                secondary:
                    "bg-gray-100 text-gray-900 hover:bg-gray-200 rounded-md border border-transparent",
                ghost:
                    "hover:bg-gray-100 rounded-md border border-transparent",
                link: "text-blue-600 underline-offset-4 hover:underline border-none",
            },
            size: {
                default: "h-9 px-4 py-2",
                sm: "h-8 gap-1.5 px-3 text-xs",
                lg: "h-10 px-6 text-base",
                icon: "h-9 w-9",
                "icon-sm": "h-8 w-8",
                "icon-lg": "h-10 w-10",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean
    loading?: boolean
    backgroundColor?: string
    showShine?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, loading, backgroundColor, showShine = false, children, style, ...props }, ref) => {
        const Comp = asChild ? Slot : "button"

        // Default background color for default variant
        const defaultBg = variant === 'default' && !backgroundColor ? '#0A66C2' : backgroundColor

        return (
            <Comp
                data-slot="button"
                className={cn(buttonVariants({ variant, size, className }), showShine && "relative overflow-hidden")}
                disabled={props.disabled || loading}
                style={{
                    ...(defaultBg && !asChild ? { backgroundColor: defaultBg } : {}),
                    ...style,
                }}
                ref={ref}
                {...props}
            >
                {asChild ? (
                    children
                ) : (
                    <>
                        {showShine && (
                            <motion.div
                                className="absolute top-0 bottom-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 w-[40%] h-full pointer-events-none select-none z-0"
                                animate={{
                                    left: ["-100%", "200%"],
                                }}
                                transition={{
                                    duration: 2.2,
                                    repeat: Infinity,
                                    repeatDelay: 1,
                                    ease: "easeInOut",
                                }}
                            />
                        )}
                        {showShine ? (
                            <span className="relative z-10 flex items-center justify-center gap-2 w-full h-full">
                                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {children}
                            </span>
                        ) : (
                            <>
                                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {children}
                            </>
                        )}
                    </>
                )}
            </Comp>
        )
    }
)
Button.displayName = "Button"

export { Button, buttonVariants }
